<template>
  <div class="modal-invoice-view-wrap">
    <portal to="dash-modal">
      <InvoiceFinanceModal
        v-if="!loading && displayFinanceModal"
        :invoice="invoice"
        :invoice-pdf="invoicePDF"
        @close="closeFinanceModal"
      />

      <!-- only for mark as sent -->
      <InvoiceSendAdjustModal 
        v-if="!showInvoicesModal && showAdjustModal && invoice"
        :invoice-id="invoice.id"
        :mark-as-sent="markedAsSent"
        @close="closeAdjustModal"
      />
    </portal>

    <!-- MAIN -->
    <div class="invoice-view-wrap">
      <div class="invoice-view--left">
        <div class="modal-form-viewselector">
          <GekkoButton
            @click.prevent="detailView = 'details'"
            :text="$gettext('Details')"
            :variant="(detailView === 'details') ? 'primary' : 'light'"
          />
          <GekkoButton
            @click.prevent="detailView = 'history'"
            :text="$gettext('History')"
            :variant="(detailView === 'history') ? 'primary' : 'light'"
          />
        </div>
        <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' }, className : 'os-theme-dark os-theme-gekko' }">
          <div v-if="detailView === 'history'" class="invoice-view-tabs--history">
            <TimelineWidget v-if="invoiceId" :widget="{ config: { object_type: 'invoice', invoice_id: invoiceId } }" />
          </div>
          <div v-if="detailView === 'details'" class="invoice-view-tabs--details">
            <div class="detail-tab--section">
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Status') }}</h4>
                <div class="d-flex">
                  <StatusIcons :item="invoice" />
                  <StatusBadge v-if="invoice" :display-status="invoice.display_status" />
                </div>
              </div>
            </div>
            <div class="detail-tab--section">
              
              <!-- TOTAL -->
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Total') }}</h4>
                <div class="total-amount">
                  {{ invoice.total_amount | currency(invoice.currency, 2) }}
                </div>
              </div>

              <!-- Number / name -->
              <div v-if="isCorrection" class="detail-tab--section__line">
                <div>{{ $gettext('Name') }}</div>
                <div class="strong">
                  {{ invoice.correction.name }}
                </div>
              </div>
              <div v-else class="detail-tab--section__line">
                <div>{{ $gettext('Invoice number') }}</div>
                <div class="strong">
                  {{ invoice.number }}
                </div>
              </div>

              <!-- Date / period -->
              <div v-if="isCorrection" class="detail-tab--section__line">
                <div>{{ $gettext('Period') }}</div>
                <div class="">
                  {{ invoice.correction.timeframe }}
                </div>
              </div>
              <div v-else class="detail-tab--section__line">
                <div>{{ $gettext('Invoice date') }}</div>
                <div class="">
                  {{ invoice.invoice_date }}
                </div>
              </div>

              <div v-if="!isCorrection" class="detail-tab--section__line">
                <div>{{ $gettext('Payment due') }}</div>
                <div class="">
                  {{ invoice.due_date }}
                </div>
              </div>
              <div v-if="isCorrection && !invoice.correction.no_customer" class="detail-tab--section__line">
                <div>{{ $gettext('Customer') }}</div>
                <div class="">
                  {{ invoice.recipient_customer.name }}
                </div>
              </div>
              <div v-if="!isCorrection" class="detail-tab--section__line">
                <div>{{ $gettext('Issued to') }}</div>
                <div class="">
                  {{ invoice.recipient_customer.name }}
                </div>
              </div>
              <div v-if="invoice.project" class="detail-tab--section__line">
                <div>{{ $gettext('Project') }}</div>
                <div class="">
                  {{ invoice.project.name }}
                </div>
              </div>
            </div>

            <div v-if="isCorrection && correctionFiles.length > 0" class="detail-tab--section">
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Files') }}</h4>
              </div>
              <div v-for="file in correctionFiles" :key="file.id" class="detail-tab--section__line">
                <div>{{ file.title }}</div>
                <div>
                  <GekkoButton 
                    @click="downloadDocument(file)"
                    icon="fa-solid fa-download"
                    variant="link" 
                    class="text-info p-0"
                    size="sm"
                  />
                </div>
              </div>
            </div>

            <div v-if="isCorrection && invoice.message" class="detail-tab--section">
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Description') }}</h4>
              </div>
              <div class="detail-tab--section__line">
                <div class="">
                  {{ invoice.message }}
                </div>
              </div>
            </div>

            <div v-if="!isCorrection" class="detail-tab--section">
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Payments') }}</h4>
                <div v-if="invoice.amount_to_be_paid && invoice.amount_to_be_paid > 0" class="">
                  <GekkoButton
                    :to="{ name: 'InvoicePayments', params: { invoiceId: invoice.id } }"
                    :text="$gettext('Add payment')"
                    variant="link"
                    class="p-0"
                    size="sm"
                  />
                </div>
                <div v-else class="">
                  <span v-if="invoice.paid_up_front">{{ $gettext('Paid upfront') }}</span>
                  <span v-else>{{ $gettext('Fully paid') }}</span>
                </div>
              </div>

              <div v-if="invoice.amount_to_be_paid && invoice.amount_to_be_paid > 0" class="detail-tab--section__line">
                <div>{{ $gettext('Pending') }}</div>
                <div class="">
                  {{ invoice.amount_to_be_paid | currency(invoice.currency, 2) }}
                </div>
              </div>
            </div>

            <div v-if="!isCorrection" class="detail-tab--section">
              <div class="detail-tab--section__line">
                <h4>{{ $gettext('Emails') }}</h4>
                <div v-if="invoice.status === 'd' || isUploaded" class="">
                  <GekkoButton
                    :to="{ name: 'InvoiceSendNew', params: { invoiceId: invoice.id } }"
                    :text="$gettext('Send via email')"
                    variant="link"
                    class="p-0"
                    size="sm"
                  />
                </div>
                <div v-else class="">
                  <GekkoButton
                    :to="{ name: 'InvoiceSendNew', params: { invoiceId: invoice.id } }"
                    :text="$gettext('Send reminder')"
                    variant="link"
                    class="p-0"
                    size="sm"
                  />
                </div>
              </div>
              <div v-if="invoice.sent_date" class="detail-tab--section__line">
                <div>{{ $gettext('Sent date') }}</div>
                <div class="">
                  {{ invoice.sent_date | formatDate('DD-MM-YYYY') }}
                </div>
              </div>
            </div>
          </div>
        </overlay-scrollbars>
      </div>

      <div class="invoice-view--right">
        <InvoicePDFPreview v-if="!isCorrection" :key="invoiceId" :invoice-id="invoiceId" />
        <div v-else class="corrections-pdf-preview-wrap">
          <dir class="corrections-pdf-preview">Preview is not available for corrections</dir>
          
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex";

import invoices from "@/mixins/invoices";

import GekkoButton from "@/components/GekkoButton"
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import StatusBadge from "@/components/invoices/ui/StatusBadge"
import StatusIcons from "@/components/invoices/ui/StatusIcons"

import TimelineWidget from "@/components/dashboard/widgets/TimelineWidget";
import InvoiceFinanceModal from "@/components/invoices/ui/InvoiceFinanceModal";
import InvoiceSendAdjustModal from "@/components/invoices/ui/InvoiceSendAdjustModal"
import InvoicePDFPreview from "@/components/invoices/ui/InvoicePDFPreview"

export default {
  name: "InvoiceAdd",
  mixins: [invoices],
  components: {
    GekkoButton,
    LoadingSpinner,
    TimelineWidget,
    InvoiceFinanceModal,
    InvoiceSendAdjustModal,
    InvoicePDFPreview,
    StatusBadge,
    StatusIcons
  },
  data () {
    return {
      invoiceId: null,
      loading: true,
      showInvoicesModal: false,
      showFinanceModal: false,
      manualFinanceModalToggle: false,
      showAdjustModal: false,
      modalType: null,
      markedAsSent: false,
      detailView: 'details'
    }
  },
  computed: {
    ...mapGetters({ 
      currentUserProfile: 'main/getCurrentUserProfile',
      currentCompany: "main/getCurrentCompany",
      currentLayout: "invoicesLayout/getCurrentLayout",
      creditableStatuses: 'invoices/getCreditableStatuses',
      invoice: "invoices/getInvoice",
      invoiceToCopy: 'invoices/getInvoiceToCopy',
      invoicePDF: "invoices/getInvoicePDF",
      currencyByCode: "main/getCurrencyByCode",
      correctionFiles: 'invoices/getCorrectionFiles',
    }),
    allowRaboFinancing () {
      return this.invoice && (this.invoice.currency === 'EUR' && (1000 <= this.invoice.total_amount && this.invoice.total_amount < 100000) && this.invoice.status === 's')
    },
    displayFinanceModal () {
      let display_modal = false
      if (this.manualFinanceModalToggle) {
        display_modal = this.showFinanceModal
      } else {
        display_modal = !this.showInvoicesModal && this.allowRaboFinancing && (this.currentUserProfile.show_rabo_notification || this.showFinanceModal)
      }
      return display_modal
    },
    isSent () {
      return this.invoice?.status === 's'
    },
    isCorrection () {
      return (this.invoice && this.invoice.correction !== null)
    },
    isUploaded () {
      return (this.invoice && this.invoice.uploaded)
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler (to) {
        this.showInvoicesModal = to.meta.showInvoicesModal
        this.modalType = to.meta.modalType
      }
    }
  },
  created () {
    this.invoiceId = this.$route.params['invoiceId']
    if (this.invoiceId) {
      this.setupData()
    }
  },
  methods: {
    ...mapActions({ 
      setInvoiceToCopy: 'invoices/setInvoiceToCopy',
    }),
    setupData () {
      if (this.invoice) {
        console.log('view this.invoice', this.invoice)
        if (this.invoice.status !== 'recurrent') {
          this.$emit('display-buttons', 'view')
        } else {
          this.$emit('display-buttons', '')
        }
      } else {
        console.log('Invoice is not set')
      }
      this.loading = false
    },
    closeInvoicesModal () {
      this.showInvoicesModal = false
      this.modalType = null
      this.$router.push({ name: 'InvoiceView', params: { invoiceId: this.invoiceId } })
    },
    closeFinanceModal () {
      this.manualFinanceModalToggle = true
      this.showFinanceModal = false
    },
    closeAdjustModal () {
      this.showAdjustModal = false
    },
    manualFinanceToggle () {
      console.log('manualFinanceToggle')
      this.manualFinanceModalToggle = true
      this.showFinanceModal = true
    }
  }
}
</script>
<style scoped lang="scss">
  .modal-invoice-view-wrap {
    height: 100%;
  }
  .invoice-view-wrap {
    display: flex;
    flex-direction: column;
    height: 100%;
    .invoice-view {
      &--left {
        width: 100%;
        display: flex;
        flex-direction: column;
        height: 100%;
        max-height: 175px;
        border-bottom: 1px solid $colorBorderDash;
        h4 { 
          margin-top: 1rem;
          margin-bottom: 0.5rem
        }
      }
      &--right {
        padding: 0;
        background: #d4d4d4;
        position: relative;
        display: flex;
        flex: 1;
        height: 100%;
        // aspect-ratio: 1 / 1.4142; // for portrait A4
        // max-width: 800px; // old styling
        // margin: 0 auto;
        .pdf-preview {
          height: 100%;
          width: 100%;
          border: 0;
        }
      }
    }
    @media (min-width: '768px') {
      flex-direction: row;
      .invoice-view {
        &--left {
          max-height: 100%;
          width: 33.33%;
          max-width: 350px;
          border-bottom: 0;
        }
        &--right {

        }
      }
    }

    .invoice-view-tabs {
      &--history {
        padding-right: 1rem;
      }
      &--details {
        padding-right: 1rem;
        .detail-tab {
          &--section {
            padding: 1rem 0;

            // border-bottom: 1px solid $colorBorderDash;
            &:not(:last-child) {
              border-bottom: 1px solid $colorBorderDash;
            }

            &__line {
              display: flex;
              justify-content: space-between;
              align-items: center;

              font-size: 14px;

              &:not(:last-child) {
                padding-bottom: 0.5rem;
              }

              h4 { margin: 0; padding: 0; }
              .total-amount {
                font-size: 22px;
                white-space: nowrap;
              }
            }
          }
        }
      }
    }

    .corrections-pdf-preview-wrap {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      .corrections-pdf-preview {
        padding: 2rem;
        border-radius: $radiusLg;
        box-shadow: $dropdownShadow;
        background: white;
        font-size: 1rem;
      }
    }

  }
</style>

<style lang="scss">
  .invoice-view-wrap {
    .modal-form-viewselector {
      display: flex;
      gap: 0.5rem;
      padding: 1rem;
      padding-left: 0rem;
      border-bottom: 1px solid $colorBorderDash;
      .btn {
        flex: 1;
        border: none;
        font-size: 14px;
        border-radius: $radiusMd;
      }
    }
    .widget-wrapper {
      &.activities-timeline {
        margin: 0;
      }
    }
  }
</style>